import type { Cell, Column, Cue } from '@rundown-studio/types'

import { getDefaultStore } from 'jotai'

import * as logger from '../../../utils/logger'

import { setCellByIdAtom } from '@/store/rundown.store'

import { rundownPageStore } from '../rundown-page.store'

import { rundownApi } from '../../../axios'
import { apiErrorHandler } from '../../../utils/api-error-handler'

import type { GridCellContent } from './cell.types'

async function updateCell(
  rundownId: string,
  cellId: string,
  payload: Partial<Cell>,
) {
  return await apiErrorHandler(
    rundownApi.patch(`/${rundownId}/cells/${cellId}`, payload),
    {
      silenceStatuses: [
        // 409 means the client's view is stale (optimistic-locking gate). Don't toast —
        // Firestore will deliver the newer state on the next snapshot.
        409,
      ],
    },
  )
}

async function createCell(rundownId: string, payload: Partial<Cell>) {
  return await apiErrorHandler(rundownApi.post(`/${rundownId}/cells`, payload))
}

export async function upsertCell(
  cell: Cell | undefined,
  columnId: Column['id'],
  cueId: Cue['id'],
  contentPayload: GridCellContent,
) {
  const {
    rundown: { id: rundownId },
  } = rundownPageStore

  const payload = contentPayload || {}

  logger.log('[Cell] upsertCell. Payload:', payload)

  // Cell exists
  if (cell?.id) {
    logger.log('[Cell] updateCell', cell.id)

    // Echo the cell's last-known `updatedAt` back to the server as the
    // optimistic-locking precondition. Server rejects with 409 if the
    // stored cell has moved on since the client received this snapshot.
    const lastSeenUpdatedAt = cell.updatedAt
    try {
      const { data } = await updateCell(rundownId, cell.id, {
        ...payload,
        updatedAt: lastSeenUpdatedAt,
      })

      // Trigger set atom
      getDefaultStore().set(setCellByIdAtom, data)

      return data
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status
      if (status === 409) {
        logger.warn('[Cell] save-rejected-409', {
          cellId: cell.id,
          lastSeenUpdatedAt:
            lastSeenUpdatedAt instanceof Date
              ? lastSeenUpdatedAt.getTime()
              : null,
        })
      }

      throw err
      // Error already toasted upstream; leave the atom untouched so the snapshot listener stays authoritative.
    }
  }

  // No cell exists, create new
  else {
    logger.log('[Cell] createCell')

    const { data } = await createCell(rundownId, {
      ...payload,
      columnId,
      cueId,
    })
    return data
  }
}
